import React, { useState, useEffect } from "react";
import { computeReductionAmount, validateReductionCode, FIXED_PRESETS, PERCENT_PRESETS } from "../../data/reductions";

const formatPrice = (v) => `${Number(v || 0).toFixed(2)} €`;

const ReductionPanel = ({
  show,
  onClose,
  subtotal,
  reduction,
  onApply,
  onClear,
}) => {
  const [tab, setTab] = useState(reduction?.type === "coupon" ? "coupon" : reduction?.type === "fixed" ? "fixed" : "percent");
  const [couponCode, setCouponCode] = useState(reduction?.couponCode || "");
  const [fixedValue, setFixedValue] = useState(reduction?.type === "fixed" ? reduction.value : "");
  const [percentValue, setPercentValue] = useState(reduction?.type === "percent" ? reduction.value : "");

  useEffect(() => {
    if (show) {
      setTab(reduction?.type === "coupon" ? "coupon" : reduction?.type === "fixed" ? "fixed" : "percent");
      setCouponCode(reduction?.couponCode || "");
      setFixedValue(reduction?.type === "fixed" ? reduction.value : "");
      setPercentValue(reduction?.type === "percent" ? reduction.value : "");
    }
  }, [show, reduction]);

  if (!show) return null;

  const couponResult = couponCode.trim()
    ? validateReductionCode(couponCode.trim(), subtotal)
    : null;

  const selection = (() => {
    if (tab === "coupon") {
      if (!couponResult?.valid) return { type: "none", value: "", couponCode: "", discount: 0, coupon: null };
      return {
        type: "coupon",
        value: couponResult.coupon.discount,
        couponCode: couponResult.coupon.code,
        discount: couponResult.coupon.discount,
        coupon: couponResult.coupon,
      };
    }
    if (tab === "fixed") {
      const v = parseFloat(fixedValue);
      if (isNaN(v) || v <= 0) return { type: "none", value: "", couponCode: "", discount: 0, coupon: null };
      return { type: "fixed", value: v, couponCode: "", discount: 0, coupon: null };
    }
    const v = parseFloat(percentValue);
    if (isNaN(v) || v <= 0) return { type: "none", value: "", couponCode: "", discount: 0, coupon: null };
    return { type: "percent", value: Math.min(v, 100), couponCode: "", discount: 0, coupon: null };
  })();

  const appliedAmount = computeReductionAmount(selection, subtotal);
  const remaining = Math.max(0, Number(subtotal || 0) - appliedAmount);

  const hasActive = reduction && reduction.type !== "none";

  const handleOk = () => {
    if (selection.type !== "none") {
      onApply(selection);
    } else if (hasActive) {
      onClear();
    }
    onClose();
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "420px" }}>
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px", overflow: "hidden" }}>
          <div className="modal-header border-bottom-0 pb-0 pt-4 px-4">
            <h5 className="modal-title fw-bold">
              <i className="bi bi-tag-fill me-2" style={{ color: "#16a34a" }}></i>Réduction
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body p-4">
            <div className="d-flex gap-2 mb-4">
              {[
                { id: "coupon", label: "Code" },
                { id: "fixed", label: "Montant fixe" },
                { id: "percent", label: "Pourcentage" },
              ].map((t) => (
                <button
                  key={t.id}
                  className={`btn btn-sm flex-grow-1 fw-bold ${tab === t.id ? "btn-dark text-white" : "btn-outline-secondary"}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "coupon" && (
              <div>
                <label className="form-label small fw-bold text-muted">Code de réduction</label>
                <input
                  type="text"
                  className={`form-control ${couponCode && couponResult?.valid ? "is-valid" : couponCode && !couponResult?.valid ? "is-invalid" : ""}`}
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Entrez le code..."
                  autoFocus
                />
                {couponCode && couponResult?.valid && (
                  <div className="valid-feedback" style={{ display: "block" }}>
                    Coupon valide: -{formatPrice(couponResult.coupon.discount)}
                  </div>
                )}
                {couponCode && !couponResult?.valid && (
                  <div className="invalid-feedback" style={{ display: "block" }}>
                    {couponResult?.error}
                  </div>
                )}
              </div>
            )}

            {tab === "fixed" && (
              <div>
                <label className="form-label small fw-bold text-muted">Montant fixe (€)</label>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {FIXED_PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`btn btn-sm ${fixedValue === String(p) ? "btn-success text-white" : "btn-outline-success"}`}
                      onClick={() => setFixedValue(String(p))}
                    >
                      {p} €
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  placeholder="Montant personnalisé..."
                  value={fixedValue}
                  onChange={(e) => setFixedValue(e.target.value)}
                />
              </div>
            )}

            {tab === "percent" && (
              <div>
                <label className="form-label small fw-bold text-muted">Pourcentage (%)</label>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {PERCENT_PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`btn btn-sm ${percentValue === String(p) ? "btn-success text-white" : "btn-outline-success"}`}
                      onClick={() => setPercentValue(String(p))}
                    >
                      {p} %
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  className="form-control"
                  placeholder="Pourcentage personnalisé..."
                  value={percentValue}
                  onChange={(e) => setPercentValue(e.target.value)}
                />
              </div>
            )}

            {/* Preview */}
            <div className="bg-light rounded-3 p-3 mt-4">
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Sous-total</span>
                <span className="fw-bold">{formatPrice(subtotal)}</span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted">Réduction</span>
                <span className="fw-bold text-success">-{formatPrice(appliedAmount)}</span>
              </div>
              <div className="d-flex justify-content-between border-top pt-2">
                <span className="fw-bold">À payer</span>
                <span className="fw-bold fs-5" style={{ color: "#166534" }}>{formatPrice(remaining)}</span>
              </div>
            </div>
          </div>

          <div className="modal-footer border-top-0 bg-light p-3 d-flex justify-content-between">
            {hasActive && (
              <button
                className="btn btn-outline-danger"
                onClick={() => { onClear(); onClose(); }}
              >
                <i className="bi bi-x-circle me-1"></i>Retirer
              </button>
            )}
            <div className="ms-auto d-flex gap-2">
              <button className="btn btn-secondary px-4" onClick={onClose}>Annuler</button>
              <button
                className="btn btn-success px-4 fw-bold"
                onClick={handleOk}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReductionPanel;